import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { getSettingValue } from 'components/company/settings/settings.registry'
import { TFunction } from 'i18next'
import { ID } from '../../utils/id'
import { AppDispatch, State } from '../index'
import { getFoundParagraphIds } from './selector'
import { MessageInstance } from 'antd/es/message/interface'

export const editorSlice = createSlice({
  initialState: () => ({
    scroll: getSettingValue<boolean>('transcript_editor_autoscroll_default'),
    selectedParagraph: undefined as ID,
    selectedSpeaker: undefined as ID,
    selectedTag: undefined as ID,
  }),
  name: 'editor',

  reducers: {
    allowScroll: (state, action: PayloadAction<boolean>) => {
      state.scroll = action.payload
      return state
    },
    selectParagraph: (state, action: PayloadAction<ID>) => {
      state.selectedParagraph = action.payload
      return state
    },
    selectSpeaker: (state, action: PayloadAction<ID>) => {
      state.selectedSpeaker = action.payload
      return state
    },
    selectTagSpeakerAndParagraph: (
      state,
      action: PayloadAction<{ tag: ID; speaker: ID; paragraph: ID}>,
    ) => {
      state.selectedTag = action.payload.tag
      state.selectedSpeaker = action.payload.speaker
      state.selectedParagraph = action.payload.paragraph
      return state
    },
    unselectTag: (state) => {
      state.selectedTag = undefined
      state.selectedSpeaker = undefined
      state.selectedParagraph = undefined
      return state
    },
  },
})

export const {
  allowScroll,
  selectParagraph,
  selectSpeaker,
  selectTagSpeakerAndParagraph,
  unselectTag,
} = editorSlice.actions


export const selectTag = createAsyncThunk<void, {
  tag: ID
  speaker: ID
  paragraph: ID
}, {
  dispatch: AppDispatch
  state: State
}>(
  'editor/selectTag',
  async (action, thunkAPI) => {
    thunkAPI.dispatch(selectTagSpeakerAndParagraph(action))

    if (!action.paragraph) {
      const paragraphs = getFoundParagraphIds(thunkAPI.getState())

      thunkAPI.dispatch(selectParagraph(paragraphs[0]))
    }
  },
)

export const findNextTag = createAsyncThunk<void, {
  message: MessageInstance
  t: TFunction
}, {
  dispatch: AppDispatch
  state: State
}>(
  'editor/findNextTag',
  (payload, thunkAPI) => {
    const { message, t } = payload
    const state = thunkAPI.getState()
    const paragraphs = getFoundParagraphIds(state)

    if (paragraphs.length === 1) {
      void message.info(t('only-tag', 'This is the only tag'))
      return
    }

    const next = paragraphs.indexOf(state.editor.selectedParagraph) + 1

    if (paragraphs.length <= next) {
      void message.info(t('end-reached', 'End reached, showing first occurrence'))

      thunkAPI.dispatch(selectParagraph(paragraphs[0]))
    } else {
      thunkAPI.dispatch(selectParagraph(paragraphs[next]))
    }
  },
)

export const findPreviousTag = createAsyncThunk<void, {
  message: MessageInstance
  t: TFunction
}, {
  dispatch: AppDispatch
  state: State
}>(
  'editor/findPreviousTag',
  (payload, thunkAPI) => {
    const { message, t } = payload
    const state = thunkAPI.getState()

    const paragraphs = getFoundParagraphIds(state)

    if (paragraphs.length === 1) {
      void message.info(t('only-tag', 'This is the only tag'))
      return
    }

    const prev = paragraphs.indexOf(state.editor.selectedParagraph) - 1

    if (prev < 0) {
      void message.info(t('earliest-reached', 'Earliest reached, showing last occurrence'))

      thunkAPI.dispatch(selectParagraph(paragraphs[paragraphs.length - 1]))
    } else {
      thunkAPI.dispatch(selectParagraph(paragraphs[prev]))
    }
  },
)
